import { useCallback, useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Landmark, Loader2, RefreshCw, Unlink, Plus, ShieldCheck } from "lucide-react";
import { api, timeAgo } from "@/lib/api";
import { toast } from "sonner";

/**
 * PlaidLinkButton — fetches a link_token on click, opens the Plaid Link flow,
 * exchanges the resulting public_token, and triggers the first transaction sync.
 */
const PlaidLinkButton = ({ onConnected, label = "Connect a bank account", compact = false }) => {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSuccess = useCallback(
    async (public_token, metadata) => {
      setLoading(true);
      try {
        const r = await api.post("/plaid/exchange", {
          public_token,
          institution_name: metadata?.institution?.name || "Connected bank",
          institution_id: metadata?.institution?.institution_id || null,
        });
        toast.success(
          `${r.data.institution_name} connected`,
          { description: `Aria imported ${r.data.imported} transactions · ${r.data.auto_approved} auto-approved, ${r.data.needs_review} to review.` }
        );
        onConnected?.(r.data);
      } catch {
        toast.error("Bank connection could not be completed", { description: "Please try connecting again." });
      } finally {
        setLoading(false);
        setToken(null);
      }
    },
    [onConnected]
  );

  const onExit = useCallback(() => {
    setLoading(false);
    setToken(null);
  }, []);

  const { open, ready } = usePlaidLink({ token, onSuccess, onExit });

  useEffect(() => {
    if (token && ready) open();
  }, [token, ready, open]);

  const start = async () => {
    setLoading(true);
    try {
      const r = await api.post("/plaid/link-token");
      setToken(r.data.link_token);
    } catch {
      toast.error("Could not start the bank connection", { description: "Check the Plaid credentials and try again." });
      setLoading(false);
    }
  };

  return (
    <Button
      size="sm"
      onClick={start}
      disabled={loading}
      data-testid="plaid-connect-btn"
      className={`h-8 gap-1.5 px-3.5 text-[12px] font-medium btn-gradient text-white ${compact ? "" : ""}`}
    >
      {loading ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
      {label}
    </Button>
  );
};

export default function PlaidConnect() {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = useCallback(
    () => api.get("/plaid/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false, connected: false, items: [], imported_total: 0 })),
    []
  );
  useEffect(() => { load(); }, [load]);

  const handleConnected = () => { load(); };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/plaid/sync");
      toast.success("Sync complete", { description: `${r.data.imported} new transactions imported.` });
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error("Sync failed", { description: detail || "Please try again." });
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async () => {
    setDisconnecting(true);
    try {
      await api.post("/plaid/disconnect");
      toast.success("Bank disconnected", { description: "Imported expenses were kept in Aria." });
      load();
    } catch {
      toast.error("Could not disconnect the bank");
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div data-testid="plaid-section">
      <div className="mb-2.5 flex items-center gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-[#6b6b6b]">Live bank feeds · Plaid</h3>
        <span className="flex items-center gap-1 rounded-full border border-[#4ADE80]/25 bg-[#4ADE80]/10 px-2 py-[1px] text-[9.5px] font-medium uppercase tracking-wide text-[#4ADE80]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E]" /> Live
        </span>
      </div>

      {!status ? (
        <Skeleton className="h-40 rounded-[14px] bg-[#141414]" />
      ) : (
        <div className="rounded-[14px] border border-[#222] bg-[#111111] p-5 card-shadow">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#4F8EF7]/30 bg-[#4F8EF7]/10 text-[#4F8EF7]">
                <Landmark size={20} />
              </div>
              <div>
                <div className="flex items-center gap-2 text-[13.5px] font-medium">
                  Bank & card connections
                  <span className="rounded-md border border-[#2a2a2a] bg-[#181818] px-1.5 py-[1px] text-[9.5px] uppercase tracking-wide text-[#6b6b6b]">
                    {status.environment || "sandbox"}
                  </span>
                </div>
                <p className="mt-1 max-w-md text-[11.5px] leading-relaxed text-[#A3A3A3]">
                  Securely link an account through Plaid. Aria pulls the real transaction feed, classifies each charge,
                  runs policy checks, and imports them straight into Expenses.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {status.connected && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={syncNow}
                  disabled={syncing}
                  data-testid="plaid-sync-btn"
                  className="h-8 gap-1.5 border-[#2a2a2a] bg-transparent px-3 text-[12px] text-[#A3A3A3] hover:bg-[#1c1c1c] hover:text-white"
                >
                  {syncing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                  Sync now
                </Button>
              )}
              <PlaidLinkButton onConnected={handleConnected} label={status.connected ? "Add another" : "Connect a bank account"} />
            </div>
          </div>

          {!status.configured && (
            <div className="mt-4 rounded-lg border border-[#F59E0B]/25 bg-[#F59E0B]/10 px-3 py-2 text-[11.5px] text-[#F59E0B]" data-testid="plaid-not-configured">
              Plaid credentials are not configured on the server.
            </div>
          )}

          <AnimatePresence initial={false}>
            {status.connected ? (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 space-y-2 overflow-hidden"
              >
                {status.items.map((it) => (
                  <div
                    key={it.item_id}
                    data-testid={`plaid-bank-item-${it.item_id}`}
                    className="flex items-center justify-between rounded-lg border border-[#1f1f1f] bg-[#0d0d0d] px-3.5 py-2.5"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-2 w-2 items-center justify-center">
                        <span className="h-2 w-2 rounded-full bg-[#22C55E]" />
                      </span>
                      <div>
                        <div className="text-[12.5px] font-medium">{it.institution_name}</div>
                        <div className="mt-0.5 text-[10.5px] text-[#6b6b6b]">
                          {it.imported_count} transactions imported · synced {timeAgo(it.last_sync)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="flex items-center justify-between pt-1">
                  <div className="flex items-center gap-1.5 text-[10.5px] text-[#6b6b6b]">
                    <ShieldCheck size={12} className="text-[#4ADE80]" />
                    <span data-testid="plaid-imported-total">{status.imported_total} expenses imported via bank feed</span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={disconnect}
                    disabled={disconnecting}
                    data-testid="plaid-disconnect-btn"
                    className="h-7 gap-1.5 px-2.5 text-[11px] text-[#EF6B6B] hover:bg-[#EF4444]/10 hover:text-[#F87171]"
                  >
                    {disconnecting ? <Loader2 size={12} className="animate-spin" /> : <Unlink size={12} />}
                    Disconnect all
                  </Button>
                </div>
              </motion.div>
            ) : (
              status.configured && (
                <div className="mt-4 flex items-center gap-1.5 rounded-lg border border-[#1f1f1f] bg-[#0d0d0d] px-3.5 py-2.5 text-[11px] text-[#6b6b6b]" data-testid="plaid-sandbox-hint">
                  <ShieldCheck size={12} className="text-[#4F8EF7]" />
                  Sandbox: search for any bank and sign in with <span className="mx-1 rounded bg-[#181818] px-1.5 py-[1px] font-mono text-[#A3A3A3]">user_good</span> / <span className="mx-1 rounded bg-[#181818] px-1.5 py-[1px] font-mono text-[#A3A3A3]">pass_good</span>
                </div>
              )
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
